import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from "../assets/logo.svg";
import LogoTransparent from "../assets/logoTransparent.svg";

import { ReactComponent as DotGroup1 } from "../assets/dotGroup1.svg";
import { ReactComponent as DotGroup2 } from "../assets/dotGroup2.svg";
import ProfessorsImage from "../assets/photoGroup2.webp";
import SearchImage from "../assets/photoGroup4.webp";
import HeroFindMatches from "./HeroFindMatches";
import FeedbackModal from "./FeedbackModal";
import { useGuestPreview } from "../context/GuestPreviewContext";

const SmallHome = () => {
    const navigate = useNavigate();
    const { isGuestPreview } = useGuestPreview();
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    const goToSignUp = () => {
        navigate('/register'); // Navigate to the signup page
    };

    const goToLoginOrApp = () => {
        // Guest keyword preview + stale authToken used to send users to /recommendations
        // in guest mode without state → preview error. Log In must open the real login page.
        if (isGuestPreview) {
            navigate('/login', { state: { fromGuestPreview: true } });
            return;
        }
        if (localStorage.getItem('authToken')) {
            navigate('/recommendations');
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <FeedbackModal
                open={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
                requireEmailCaptcha
            />
            {/* Navbar */}
            <nav className="bg-white border-b-[0.5px] border-[#202531] z-50 relative">
                <div className="max-w-full px-[16px] py-2.5">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="flex items-center" title="Home">
                            <img
                                src={Logo}
                                alt="Grad Connect"
                                className="h-[38px]"
                            />
                        </Link>
                        <button
                            type="button"
                            onClick={goToLoginOrApp}
                            className="text-sm font-medium text-[#202531] transition hover:text-[#0196BF]"
                        >
                            Log In
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative isolate flex flex-col justify-center items-center px-8 sm:px-16 lg:px-32 pt-12 pb-10 bg-gray-50">
                <div className="absolute bottom-2 right-4 w-[20vw] h-[20vw] z-0 opacity-100 pointer-events-none transform rotate-[85deg]">
                    <DotGroup1 className="w-full h-full" />
                </div>
                <div className="w-full max-w-none text-left relative z-10 flex flex-col justify-center items-start">
                    <p
                        className="text-xl leading-tight sm:text-2xl text-[#434D5C] mt-0 mb-2 max-w-[380px]"
                    >
                        Success in grad school begins with the right advisor.
                    </p>

                    <motion.h1


                        className="text-2xl sm:text-3xl font-bold leading-tight text-gray-900 mt-1 mb-0 max-w-[380px]"


                        initial={{ opacity: 0 }}


                        animate={{ opacity: 1 }}


                        transition={{ duration: 1.1, delay: 0.35, ease: "easeOut" }}


                    >


                        Grad Connect helps you find your perfect match.


                    </motion.h1>

                    <div className="w-full -mt-3">
                        <HeroFindMatches layout="normal" buttonBelow />
                    </div>
                </div>
            </section>

{/* Features Section */}
<section className="relative isolate px-8 sm:px-16 lg:px-32 py-10 bg-gray-50">
    <div className="lg:flex justify-between items-center mb-8 sm:mb-0">
        <div
            className="relative z-10 lg:w-1/2 text-left max-w-screen-lg mx-auto"
        >
            {/* Centered Heading */}
            <h2
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center"
            >
                Every Professor in One Place
            </h2>

            {/* Left-aligned text centered in the container */}
            <div className="max-w-[480px] mx-auto">
                <p className="text-lg sm:text-xl text-gray-700 mb-8">
                    No more searching school directories. Grad Connect has all the information you need to evaluate advisors from every university in the US.
                </p>

                {/* Features List */}
                <div className="space-y-6">
                    <div
                        className="flex items-center max-w-[440px] mx-auto"
                    >
                        <span className="text-[#FDA800] text-3xl mr-3">✔</span>
                        <p className="text-lg sm:text-xl text-gray-700">Clear Research Summaries</p>
                    </div>
                    <div
                        className="flex items-center max-w-[440px] mx-auto"
                    >
                        <span
                            className="text-[#E50C33] text-3xl mr-3 self-start"
                            style={{ transform: 'translateY(30%)' }}
                        >
                            ✔
                        </span>
                        <p className="text-lg sm:text-xl text-gray-700">
                            Links to Personal Website and Google Scholar Profile
                        </p>
                    </div>
                    <div
                        className="flex items-center max-w-[440px] mx-auto"
                    >
                        <span className="text-[#0196BF] text-3xl mr-3">✔</span>
                        <p className="text-lg sm:text-xl text-gray-700">Contact Information</p>
                    </div>
                </div>
            </div>
        </div>

    </div>
</section>


{/* Intelligent Recommendations Section */}
<section className="relative isolate px-8 sm:px-16 lg:px-32 -mt-12 py-12 bg-gray-50">
    <div className="lg:flex justify-between items-center mb-8 sm:mb-0">
        <div className="relative z-10 lg:w-1/2 text-left max-w-[480px] mx-auto sm:mx-0">
            <h2
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center"
            >
                Intelligent Recommendations
            </h2>
            <p
                className="text-lg sm:text-xl text-gray-700 max-w-[480px] mb-8"
            >
                Grad Connect helps you discover professors whose research aligns with your interests—even ones from schools you’ve never considered.
            </p>
            <p
                className="text-lg sm:text-xl max-w-[480px] text-gray-700"
            >
                The more you interact with professor profiles, the better the recommendations become.
            </p>
        </div>
    </div>
</section>

{/* Organize Your Search Section */}
<section className="px-8 sm:px-16 lg:px-32 -mt-12 py-12 bg-gray-50">
    <div className="max-w-screen-lg mx-auto text-center">
        {/* Centered Header */}
        <h2
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6"
        >
            Organize Your Search
        </h2>

        {/* Left-Aligned Paragraphs */}
        <div className="text-left max-w-[480px] mx-auto">
            <p
                className="text-lg sm:text-xl text-gray-700 mb-6"
            >
                Grad Connect lets you search for professors by name, school, or research topic. You can filter results by school or department, and easily bookmark professors you're interested in.
            </p>
            <p
                className="text-lg sm:text-xl text-gray-700 mb-6"
            >
                All their information in one place, making it simple to reach out when you're ready.
            </p>
        </div>
    </div>

    {/* Image Container */}
    <div className="flex justify-center mt-4 p-0">
        <img
            src={SearchImage}
            alt="Search and organize professors"
            className="w-full h-auto max-w-[420px]"
        />
    </div>
</section>





{/* Final Call to Action */}
<div className="border-t-[0.8px] border-[#202531] w-screen mb-12"></div>

<section className="px-8 sm:px-16 lg:px-32 -mt-8 pt-2 pb-10 bg-gray-50 relative isolate">
    <div className="relative z-10 max-w-screen-lg mx-auto text-center">
        <h2
            className="text-xl sm:text-2xl font-medium text-gray-900 mb-2"
        >
            The Best Part?
        </h2>

        <h3
            className="text-2xl sm:text-3xl font-bold leading-snug text-gray-900 mb-4"
        >
            Grad Connect is <span className="text-[#E50C33]">free</span> for students.
        </h3>

        {/* Static button without animation */}
        <button
            onClick={goToSignUp}
            className="bg-[#0196BF] text-white font-bold text-xl sm:text-2xl px-4 sm:px-6 py-2 sm:py-3 rounded-full hover:bg-[#017BA3] transition"
        >
            Get Started
        </button>
    </div>
</section>


{/* Footer */}
<div className="border-t-[0.8px] border-[#202531] w-screen"></div>

<footer className="bg-white pt-4 pb-8 mb-0">
    <div className="mx-auto px-6 text-center">
        {/* Logo */}
        <img src={LogoTransparent} alt="Logo" className="h-[38px] mx-auto mb-4" />

        {/* Links */}
        <div className="space-y-2 mb-6">
            <a href="/register" className="text-sm font-normal text-[#202531] hover:text-[#0196BF] transition block">
                Sign Up
            </a>
            <a href="/login" className="text-sm font-normal text-[#202531] hover:text-[#0196BF] transition block">
                Sign In
            </a>
            <button
                type="button"
                onClick={() => setIsFeedbackOpen(true)}
                className="text-sm font-normal text-[#202531] hover:text-[#0196BF] transition block w-full"
            >
                Give feedback
            </button>
        </div>

        {/* Copyright */}
        <p className="text-xs text-gray-600">
            &copy; 2026 GradConnect LLC
        </p>
    </div>
</footer>



        </div>
    );
};

export default SmallHome;
